<template>
    <section
        v-if="directionData"
        class="scroll-m-25">
        <div class="items-end justify-between md:flex">
            <AtomTitle>
                {{ directionData.name }}
            </AtomTitle>
            <BaseLink
                v-if="directionData.page.slug"
                :to="'/' + directionData.page.slug"
                icon-right="arrow-right"
                size="accent"
                open-in-new-tab
                class="max-h-11 !text-14/148 max-md:mt-5 max-md:w-full md:!text-16/120">
                Страница
                {{ isCromi() ? 'направления' : 'услуги' }}
            </BaseLink>
        </div>
        <div
            v-if="directionData.description"
            v-html="directionData.description"
            class="tinyeditor mt-3 text-16/148 text-accent-100 max-md:hidden" />
        <div
            v-if="directionData.products.data.length"
            class="relative mt-5 md:mt-10">
            <div
                class="absolute left-0 top-1/2 h-10 w-[85vw] -translate-x-[8rem] -translate-y-1/2 transform-gpu animate-slow-pulse rounded-full bg-accent/50 blur-[10rem] delay-700 sm:w-[80vw] md:w-[85vw] lg:-translate-x-1/3 xl:-translate-x-1/2" />
            <div
                :class="isCromi() ? '!pr-5' : ''"
                class="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-3 xl:grid-cols-4">
                <CatalogCategoryProduct
                    v-for="product in directionData.products.data"
                    :key="product.slug"
                    :product />
            </div>
        </div>
        <div class="mt-5 space-y-10 md:mt-10 md:space-y-20">
            <template v-if="hasProducts">
                <div
                    v-for="subcategory in subcategories"
                    :key="subcategory.slug"
                    v-show="subcategory.products.data.length">
                    <div
                        :id="subcategory.slug"
                        class="flex scroll-m-25 items-center max-md:justify-between">
                        <AtomTitle
                            level="3"
                            class="max-md:!text-16/120">
                            {{ subcategory.name }}
                        </AtomTitle>
                        <BaseLink
                            v-if="
                                directionData.child_category.data.find(
                                    (child_category) =>
                                        child_category.slug === subcategory.slug
                                ) &&
                                subcategory.page.parent_slug &&
                                subcategory.page.slug
                            "
                            :to="`/${subcategory.page.parent_slug}/${subcategory.page.slug}`"
                            icon-right="arrow-right"
                            appearance="dark"
                            open-in-new-tab
                            class="ml-3 h-auto max-md:!text-14/148 md:ml-5">
                            Подробнее
                        </BaseLink>
                    </div>
                    <div
                        v-html="subcategory.description"
                        class="mt-3 text-16/148 text-accent-100 max-md:hidden" />
                    <div class="relative mt-5 md:mt-10">
                        <div
                            class="absolute left-0 top-1/2 h-10 w-[85vw] -translate-x-[8rem] -translate-y-1/2 transform-gpu animate-slow-pulse rounded-full bg-accent/50 blur-[10rem] delay-700 sm:w-[80vw] md:w-screen lg:-translate-x-1/3 xl:-translate-x-1/2" />
                        <div
                            :class="isCromi() ? '!pr-5' : ''"
                            class="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-3 xl:grid-cols-4">
                            <CatalogCategoryProduct
                                v-for="product in subcategory.products.data"
                                :key="product.slug"
                                :product />
                        </div>
                    </div>
                </div>
            </template>
            <div v-else>
                <AtomTitle level="3">Сейчас здесь нет товаров</AtomTitle>
                <p class="mt-3 text-14/148 text-accent-200 md:text-16/148">
                    Вы&nbsp;можете позвонить или написать нам&nbsp;&mdash;
                    поможем подобрать необходимое оборудование
                </p>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseLink from '~/components/base/base-link/BaseLink.vue'
import CatalogCategoryProduct from '~/components/pages/catalog/catalog-category/CatalogCategoryProduct.vue'
import type {
    ICatalogCategory,
    ICatalogSubcategory,
} from '~/components/pages/catalog/catalog-category/catalog-category.types'

const props = defineProps({
    directionData: {
        type: Object as PropType<ICatalogCategory>,
        required: true,
    },
    hideOptionalCategories: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

if (props.directionData.child_category.data) {
    props.directionData.child_category.data.map((category) => {
        const schemaOrg = category?.schema_org?.[0]?.[0]

        if (!schemaOrg) return

        useSchemaOrg(schemaOrg as Parameters<typeof useSchemaOrg>[0])
    })
}

const subcategories = computed<ICatalogSubcategory[]>(() => {
    if (!props.directionData) return []

    const optional_categories = props.hideOptionalCategories
        ? []
        : props.directionData.optional_categories.data

    return [...props.directionData.child_category.data, ...optional_categories]
})

const hasProducts = computed<boolean>(() => {
    if (!props.directionData) return false

    const categoryWithProducts = subcategories.value.find(
        (subcategory: ICatalogSubcategory) =>
            subcategory.products.data.length > 0
    )

    return (
        !!categoryWithProducts || props.directionData.products.data.length > 0
    )
})
</script>
